import type { JiraFieldOptionWeight } from '@jira/polaris__domain-field/src/FieldOption.types.tsx';
import type { FieldKey } from '@jira/polaris__domain-field/src/FieldKey.types.tsx';

import { fireCompoundAnalyticsEvent } from '@jira/polaris__lib-analytics/src/fireCompoundAnalyticsEvent.tsx';

import { sanitizeOptionWeights } from '@jira/polaris__component-field-option-configuration/src/sanitizeOptionWeights.tsx';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import type { StoreActionApi } from '@atlassian/react-sweet-state';
import type { Props, State } from './FieldsUpdate.types.tsx';

export const createFieldOptionWeightBulk =
	(fieldKey: FieldKey, options: JiraFieldOptionWeight[]) =>
	async (
		{ getState, setState }: StoreActionApi<State>,
		{ onFieldUpdateFailed, projectId, createAnalyticsEvent, fieldRemote }: Props,
	): Promise<void> => {
		if (projectId === undefined) {
			return;
		}

		const state = getState();
		const field = state.fields[fieldKey];
		const previousOptions = field.options;
		const optionIds = previousOptions?.map((option) => option.id) || [];

		const newOptions = previousOptions?.map((option) => {
			const newValues = options.find((o) => o.optionId.toString() === option.jiraOptionId);
			if (newValues) {
				return {
					...option,
					weight: newValues.weight,
				};
			}
			return option;
		});

		const oldConfigurationOptions = field.configuration?.optionWeights ?? [];
		const newConfigurationOptions = sanitizeOptionWeights(options, optionIds);

		const fields = {
			...state.fields,
			[fieldKey]: {
				...field,
				options: newOptions,
				configuration: {
					...state.fields[fieldKey].configuration,
					optionWeights: newConfigurationOptions,
				},
			},
		};

		setState({ fields });

		return fieldRemote
			.updateFieldConfiguration({
				fieldKey,
				oldConfiguration: {},
				configuration:
					newConfigurationOptions.length > 0 ? { optionWeights: newConfigurationOptions } : {},
				type: 'updateFieldOptionWeight',
			})
			.then(() => {
				fireCompoundAnalyticsEvent.FieldConfig.optionWeightChanged(createAnalyticsEvent({}), {
					issueFieldKey: fieldKey,
					issueFieldType: fields[fieldKey].type,
				});
			})
			.catch((err) => {
				const currentFields = { ...getState().fields };

				currentFields[fieldKey] = {
					...currentFields[fieldKey],
					options: previousOptions,
					configuration: {
						...currentFields[fieldKey].configuration,
						optionWeights: oldConfigurationOptions,
					},
				};
				setState({ fields: currentFields });

				onFieldUpdateFailed(err);
			});
	};
